import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { EvaluationExam } from '@interactive-room';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './primitives/Dialog';
import {
  consoleLaunchMaterialLabel,
  listConsoleLaunchableExams,
  studioHubUrl,
} from '../../studio/consoleStudioLinks';

interface ExamLaunchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLaunch: (exam: EvaluationExam) => void;
  subjectId?: string;
}

export function ExamLaunchSheet({
  open,
  onOpenChange,
  onLaunch,
  subjectId,
}: ExamLaunchSheetProps) {
  const launchable = useMemo(() => listConsoleLaunchableExams(), [open]);
  const studioHref = studioHubUrl(subjectId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sala-dialog--studio sala-dialog--compact">
        <DialogHeader>
          <DialogTitle>Transmitir material preparado</DialogTitle>
          <DialogDescription>
            Clases interactivas y evaluaciones mixtas listas para proyectar o aplicar en vivo.
          </DialogDescription>
        </DialogHeader>

        <div className="sala-dialog__body">
          {launchable.length === 0 ? (
            <div className="sala-empty-state py-6">
              <p className="sala-empty-state__title">Sin material proyectable</p>
              <p className="sala-empty-state__hint">
                No tienes clases interactivas o evaluaciones preparadas. Créalas en tu Studio.
              </p>
              <Link
                to={studioHref}
                className="sala-btn-hero mt-4 inline-flex text-xs"
                onClick={() => onOpenChange(false)}
              >
                Ir a Studio
              </Link>
            </div>
          ) : (
            <>
              <ul className="max-h-64 space-y-2 overflow-y-auto">
                {launchable.map((exam) => (
                  <li key={exam.id}>
                    <button
                      type="button"
                      className="sala-draft-row flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:border-[var(--sala-accent)]"
                      onClick={() => {
                        onLaunch(exam);
                        onOpenChange(false);
                      }}
                    >
                      <span className="text-lg" aria-hidden>
                        {exam.deliveryFormat === 'interactive' ? '📽️' : '📋'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{exam.title}</p>
                        <p className="text-[10px] text-[var(--sala-ink-dim)]">
                          {exam.blocks.length} bloques · {consoleLaunchMaterialLabel(exam)}
                          {exam.settings.integrityMode === 'STRICT' ? ' · 🔒 Estricto' : ''}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
              <Link
                to={studioHref}
                className="sala-btn-outline mt-4 inline-flex w-full justify-center text-xs"
                onClick={() => onOpenChange(false)}
              >
                Crear material en Studio
              </Link>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
